/* eslint-disable react-hooks/rules-of-hooks, react-hooks/exhaustive-deps */
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { SHIFTIQ_API, getAuthHeaders, isAbortError } from "../../api/client";
import { usePgStores } from "../../stores/StoresProvider";
import { Icon, LoadingPulse } from "../../ui";
import { userEmployeeCode } from "../../lib/userDisplay";
import { userStorePinLabel, defaultBuilderStore } from "../../theme";
import { historyTitle, pageFromPathname, rememberHistoryEntry, searchForStorePage, storeParamFromSearch } from "../../routing/paths";
import { combineSignals, pageSignal } from "../../routing/pageAbort";
import { locationIdForStore, storeNameForLocationId } from "../../lib/storeScope";
import { resolveSharedStoreName, rememberStoreByName } from "../../lib/selectedStore";
import { announcementStoreIdForApi, announcementEditorQueryParams, announcementFetchStoreParam, mapAnnouncementFromApi } from "../../lib/announcements";
import { isEmployeeRole, normalizeSessionRole } from "../../identity/access";

import { AnnouncementCard } from "./AnnouncementCard";
export function AnnouncementsPage({ user, lang }) {
  const { storesWithAll, storeNames, storeRecords, loaded: storesLoaded } = usePgStores();
  const [items, setItems] = useState([]);
  const mountSearch = useRef(
    typeof window !== "undefined" ? window.location.search : ""
  ).current;
  const initialAnnQuery = useRef(storeParamFromSearch(mountSearch)).current;
  const role = normalizeSessionRole(user.role);
  const [filter, setFilter] = useState(() => {
    if (isEmployeeRole(user)) return "All Stores";
    if (role === "store_manager" && user.store) return user.store;
    const shared = resolveSharedStoreName({
      search: typeof window !== "undefined" ? window.location.search : "",
      storeRecords,
      storeNames,
    });
    if (role === "admin" || role === "brand_manager" || role === "hr") {
      return shared || defaultBuilderStore(storeNames, storeRecords) || "All Stores";
    }
    return shared || "All Stores";
  });
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");
  const [saving, setSaving] = useState(false);
  const employeeCode = userEmployeeCode(user);
  const canPost = ["store_manager", "admin", "hr"].includes(role);
  const isCrew = isEmployeeRole(user);
  const canShowReadCount = ["store_manager", "admin", "hr"].includes(role);
  const isCrossStore = role === "admin" || role === "brand_manager" || role === "hr";
  const defaultFormStore =
    role === "store_manager"
      ? user.store
      : isCrossStore
        ? "All Stores"
        : (user.store || storeNames[0] || "");
  const emptyForm = { title: "", body: "", store: defaultFormStore, pinned: false, photo: "", expiresAt: "" };
  const [form, setForm] = useState(emptyForm);
  const [imageChanged, setImageChanged] = useState(false);
  const [imageCleared, setImageCleared] = useState(false);
  const [, setEditingOriginalPhoto] = useState("");
  const announcementImageInputRef = useRef(null);
  const ANNOUNCEMENT_IMAGE_MAX_BYTES = 2 * 1024 * 1024;
  const ANNOUNCEMENT_IMAGE_ACCEPT = "image/jpeg,image/png,image/webp";

  const appliedAnnStore = useRef(false);
  useEffect(() => {
    if (!storesLoaded || isCrew || user.role === "store_manager" || appliedAnnStore.current) return;
    if (!storeNames.length) return;
    appliedAnnStore.current = true;
    const preferred = resolveSharedStoreName({
      search: typeof window !== "undefined" ? window.location.search : "",
      storeRecords,
      storeNames,
    }) || defaultBuilderStore(storeNames, storeRecords) || "All Stores";
    setFilter(preferred);
  }, [storesLoaded, storeRecords, storeNames, isCrew, user.role]);

  const wroteAnnUrl = useRef(false);
  useEffect(() => {
    if (isCrew || pageFromPathname(window.location.pathname) !== "announcements") return undefined;
    if (!storesLoaded) return undefined;
    const pendingName = initialAnnQuery.locationId != null
      ? storeNameForLocationId(initialAnnQuery.locationId, storeRecords)
      : "";
    if (pendingName && filter === "All Stores") return undefined;
    const locationId = filter === "All Stores" ? null : locationIdForStore(filter, storeRecords);
    const search = searchForStorePage({ locationId });
    const current = `${window.location.pathname}${window.location.search}`;
    if (current === `/announcements${search}`) {
      wroteAnnUrl.current = true;
      document.title = historyTitle("/announcements", search);
      return undefined;
    }
    rememberHistoryEntry("/announcements", search, { push: wroteAnnUrl.current });
    wroteAnnUrl.current = true;
    return undefined;
  }, [isCrew, storesLoaded, filter, storeRecords]);

  useEffect(() => {
    function onPop() {
      if (pageFromPathname(window.location.pathname) !== "announcements") return;
      const query = storeParamFromSearch(window.location.search);
      wroteAnnUrl.current = true;
      if (query.locationId != null) {
        const name = storeNameForLocationId(query.locationId, storeRecords);
        setFilter(name || "All Stores");
        return;
      }
      setFilter(query.storeName && query.storeName !== "All Stores" ? query.storeName : "All Stores");
    }
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [storeRecords]);

  const announcementStoreChips = useMemo(() => {
    if (isCrew) return [];
    if (user.role === "store_manager") {
      return [...new Set(["All Stores", user.store].filter(Boolean))];
    }
    return storesWithAll;
  }, [isCrew, user.role, user.store, storesWithAll]);

  const formStoreOptions = useMemo(() => {
    if (user.role === "store_manager") {
      return [user.store].filter(Boolean);
    }
    if (isCrossStore) {
      return ["All Stores", ...storeNames];
    }
    return storeNames;
  }, [user.role, user.store, isCrossStore, storeNames]);

  const loadAnnouncements = useCallback((signal) => {
    const storeParam = announcementFetchStoreParam(user, filter);
    const params = new URLSearchParams({
      store_id: storeParam,
      employee_code: employeeCode,
    });
    setLoading(true);
    setFetchError("");
    fetch(`${SHIFTIQ_API}/announcements?${params.toString()}`, { headers: getAuthHeaders(), signal })
      .then((r) => {
        if (!r.ok) throw new Error(String(r.status));
        return r.json();
      })
      .then((rows) => {
        if (signal.aborted) return;
        const mapped = (Array.isArray(rows) ? rows : []).map(mapAnnouncementFromApi);
        mapped.sort((x, y) => (y.pinned ? 1 : 0) - (x.pinned ? 1 : 0));
        setItems(mapped);
      })
      .catch((err) => {
        if (signal.aborted || isAbortError(err)) return;
        setFetchError(lang === "gr" ? "Αποτυχία φόρτωσης ανακοινώσεων" : "Failed to load announcements");
        setItems([]);
      })
      .finally(() => {
        if (!signal.aborted) setLoading(false);
      });
  }, [user, filter, employeeCode, lang]);

  useEffect(() => {
    const local = new AbortController();
    loadAnnouncements(combineSignals(local.signal, pageSignal()));
    return () => local.abort();
  }, [loadAnnouncements]);

  function canEditAnn(a) {
    if (!canPost) return false;
    if (user.role === "hr" || user.role === "admin" || user.role === "brand_manager") return true;
    const author = String(a.author || "").trim().toLowerCase();
    const me = String(user.name || user.email || "").trim().toLowerCase();
    return Boolean(author) && author === me;
  }

  function resetAnnouncementImageState() {
    setImageChanged(false);
    setImageCleared(false);
    setEditingOriginalPhoto("");
    if (announcementImageInputRef.current) announcementImageInputRef.current.value = "";
  }

  function openNew() {
    setEditingId(null);
    setForm(emptyForm);
    resetAnnouncementImageState();
    setDeleteConfirmId(null);
    setShowModal(true);
  }

  function openEdit(a) {
    setEditingId(a.id);
    setForm({
      title: a.title,
      body: a.body,
      store: a.store,
      pinned: a.pinned,
      photo: a.photo || "",
      expiresAt: a.expiresAt ? String(a.expiresAt).slice(0, 10) : "",
    });
    setEditingOriginalPhoto(a.photo || "");
    setImageChanged(false);
    setImageCleared(false);
    if (announcementImageInputRef.current) announcementImageInputRef.current.value = "";
    setDeleteConfirmId(null);
    setShowModal(true);
  }

  function handleAnnouncementImageSelect(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const accepted = ["image/jpeg", "image/png", "image/webp"];
    if (!accepted.includes(file.type)) {
      setFetchError(
        lang === "gr"
          ? "Επιτρέπονται μόνο JPEG, PNG ή WebP"
          : "Only JPEG, PNG, or WebP images are allowed"
      );
      e.target.value = "";
      return;
    }
    if (file.size > ANNOUNCEMENT_IMAGE_MAX_BYTES) {
      setFetchError(lang === "gr" ? "Η εικόνα είναι πολύ μεγάλη — μέγιστο 2MB" : "Image too large — max 2MB");
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = typeof reader.result === "string" ? reader.result : "";
      if (!dataUrl) return;
      setForm((prev) => ({ ...prev, photo: dataUrl }));
      setImageChanged(true);
      setImageCleared(false);
      setFetchError("");
    };
    reader.onerror = () => {
      setFetchError(lang === "gr" ? "Αποτυχία ανάγνωσης εικόνας" : "Failed to read image file");
      e.target.value = "";
    };
    reader.readAsDataURL(file);
  }

  function removeAnnouncementImage() {
    setForm((prev) => ({ ...prev, photo: "" }));
    setImageChanged(false);
    setImageCleared(true);
    if (announcementImageInputRef.current) announcementImageInputRef.current.value = "";
  }

  const markRead = useCallback(
    (announcementId) => {
      if (!employeeCode) return;
      fetch(`${SHIFTIQ_API}/announcements/${announcementId}/read`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ employee_code: employeeCode }),
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (!data) return;
          setItems((prev) =>
            prev.map((item) =>
              item.id === announcementId
                ? {
                    ...item,
                    readByMe: true,
                    readCount: Number(data.read_count) || item.readCount,
                  }
                : item
            )
          );
        })
        .catch(() => {});
    },
    [employeeCode]
  );

  async function handleSave() {
    if (!form.title.trim() || saving) return;
    setSaving(true);
    try {
      if (editingId) {
        const params = announcementEditorQueryParams(user);
        const patchBody = {
          title: form.title.trim(),
          body: form.body.trim(),
          is_pinned: form.pinned,
          expires_at: form.expiresAt || null,
        };
        if (imageCleared) {
          patchBody.image_url = null;
        } else if (imageChanged && form.photo?.trim()) {
          patchBody.image_url = form.photo.trim();
        }
        const res = await fetch(`${SHIFTIQ_API}/announcements/${editingId}?${params.toString()}`, {
          method: "PATCH",
          headers: getAuthHeaders(),
          body: JSON.stringify(patchBody),
        });
        if (!res.ok) throw new Error(String(res.status));
        const updated = mapAnnouncementFromApi(await res.json());
        setItems((prev) => {
          const next = prev.map((a) => (a.id === editingId ? { ...a, ...updated } : a));
          next.sort((x, y) => (y.pinned ? 1 : 0) - (x.pinned ? 1 : 0));
          return next;
        });
      } else {
        const res = await fetch(`${SHIFTIQ_API}/announcements`, {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify({
            title: form.title.trim(),
            body: form.body.trim(),
            store_id: announcementStoreIdForApi(form.store),
            image_url: form.photo?.trim() || null,
            is_pinned: form.pinned,
            expires_at: form.expiresAt || null,
            published_by: user.name,
            published_by_role: user.role,
          }),
        });
        if (!res.ok) throw new Error(String(res.status));
        const created = mapAnnouncementFromApi(await res.json());
        setItems((prev) => [created, ...prev].sort((x, y) => (y.pinned ? 1 : 0) - (x.pinned ? 1 : 0)));
      }
      setForm(emptyForm);
      setEditingId(null);
      resetAnnouncementImageState();
      setShowModal(false);
    } catch {
      setFetchError(lang === "gr" ? "Αποτυχία αποθήκευσης" : "Failed to save announcement");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    const params = announcementEditorQueryParams(user);
    if (!params.get("editor_name") || !params.get("editor_role")) {
      setFetchError(lang === "gr" ? "Αποτυχία διαγραφής" : "Failed to delete announcement");
      return;
    }
    try {
      const res = await fetch(`${SHIFTIQ_API}/announcements/${id}?${params.toString()}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error(String(res.status));
      setItems((prev) => prev.filter((a) => a.id !== id));
      setDeleteConfirmId(null);
    } catch {
      setFetchError(lang === "gr" ? "Αποτυχία διαγραφής" : "Failed to delete announcement");
    }
  }

  return (
    <div className="anim-fade">

      {/* Lightbox */}
      {lightbox && (
        <div className="photo-lightbox" onClick={() => setLightbox(null)}>
          <button className="photo-lightbox-close" onClick={() => setLightbox(null)}>✕</button>
          <img src={lightbox} alt="Announcement" onClick={e => e.stopPropagation()} />
        </div>
      )}

      {/* Store filter */}
      <div className="filter-row filter-split">
        <div className="filter-split-chips">
          {!isCrew && announcementStoreChips.map(s => (
            <button key={s} type="button" className={`filter-chip ${filter === s ? "chip-active" : ""}`} onClick={() => {
              setFilter(s);
              rememberStoreByName(s, storeRecords);
            }}>
              {s === "All Stores" ? (lang === "gr" ? "Όλα" : "All") : s}
            </button>
          ))}
          {isCrew && (
            <span className="info-pill">📍 {userStorePinLabel(user)} + {lang === "gr" ? "Αλυσίδα" : "Chain-wide"}</span>
          )}
        </div>
        {canPost && (
          <button type="button" className="btn-base btn-lime btn-small filter-split-action" onClick={openNew}>
            <Icon name="plus" size={14} />{lang === "gr" ? "Νέα Ανακοίνωση" : "New Announcement"}
          </button>
        )}
      </div>

      {fetchError && (
        <p style={{ color: "var(--danger)", fontSize: 13, marginBottom: 12 }}>{fetchError}</p>
      )}

      {loading ? (
        <div className="card-grid">
          {[1, 2, 3].map((n) => (
            <div key={n} className="ann-card" style={{ minHeight: 140 }}>
              <div style={{ height: 12, width: "40%", borderRadius: 8, background: "var(--surface3)", marginBottom: 12, animation: "pulse 1.2s ease-in-out infinite" }} />
              <div style={{ height: 16, width: "70%", borderRadius: 8, background: "var(--surface3)", marginBottom: 10, animation: "pulse 1.2s ease-in-out infinite" }} />
              <div style={{ height: 10, width: "100%", borderRadius: 6, background: "var(--surface3)", marginBottom: 6, animation: "pulse 1.2s ease-in-out infinite" }} />
              <div style={{ height: 10, width: "85%", borderRadius: 6, background: "var(--surface3)", animation: "pulse 1.2s ease-in-out infinite" }} />
            </div>
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="empty-state"><Icon name="megaphone" size={40} /><p>{lang === "gr" ? "Δεν υπάρχουν ανακοινώσεις" : "No announcements yet"}</p></div>
      ) : (
        <div className="card-grid">
          {items.map(a => (
            <AnnouncementCard
              key={a.id}
              a={a}
              lang={lang}
              canShowReadCount={canShowReadCount}
              canEditAnn={canEditAnn(a)}
              deleteConfirmId={deleteConfirmId}
              setDeleteConfirmId={setDeleteConfirmId}
              onEdit={openEdit}
              onDelete={handleDelete}
              onRead={markRead}
              setLightbox={setLightbox}
            />
          ))}
        </div>
      )}

      {/* Create / Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal-box">
            <div className="modal-handle" />
            <div className="modal-title">
              {editingId
                ? (lang === "gr" ? "✏️ Επεξεργασία Ανακοίνωσης" : "✏️ Edit Announcement")
                : ("📢 " + (lang === "gr" ? "Νέα Ανακοίνωση" : "New Announcement"))
              }
            </div>

            <div className="form-group">
              <label className="form-label">Title / Τίτλος</label>
              <input className="form-input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Title..." />
            </div>

            <div className="form-group">
              <label className="form-label">Message / Μήνυμα</label>
              <textarea className="form-textarea" rows={3} value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} placeholder="Write your announcement..." />
            </div>

            <div className="form-group">
              <label className="form-label">{lang === "gr" ? "Εικόνα (προαιρετικό)" : "Image (optional)"}</label>
              {form.photo ? (
                <div className="photo-preview-wrap">
                  <img src={form.photo} alt="" />
                  <button
                    type="button"
                    className="photo-remove-btn"
                    title={lang === "gr" ? "Αφαίρεση εικόνας" : "Remove image"}
                    onClick={removeAnnouncementImage}
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <div className="photo-upload-zone">
                  <input
                    ref={announcementImageInputRef}
                    type="file"
                    accept={ANNOUNCEMENT_IMAGE_ACCEPT}
                    onChange={handleAnnouncementImageSelect}
                  />
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)" }}>
                    {lang === "gr" ? "Ανέβασμα εικόνας" : "Upload image"}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>
                    JPEG, PNG, WebP · max 2MB
                  </div>
                </div>
              )}
              {editingId && form.photo ? (
                <button
                  type="button"
                  className="btn-base btn-outline btn-small"
                  style={{ marginTop: 8 }}
                  onClick={removeAnnouncementImage}
                >
                  {lang === "gr" ? "Αφαίρεση εικόνας" : "Remove image"}
                </button>
              ) : null}
            </div>

            <div className="form-group">
              <label className="form-label">Store / Κατάστημα</label>
              <select
                className="form-select"
                value={form.store}
                disabled={user.role === "store_manager"}
                onChange={e => setForm({ ...form, store: e.target.value })}
              >
                {formStoreOptions.map(s => (
                  <option key={s} value={s}>
                    {s === "All Stores" ? (lang === "gr" ? "Όλα τα καταστήματα" : "All Stores") : s}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">{lang === "gr" ? "Λήξη (προαιρετικό)" : "Expires (optional)"}</label>
              <input type="date" className="form-input" value={form.expiresAt} onChange={e => setForm({ ...form, expiresAt: e.target.value })} />
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
              <input type="checkbox" id="pin-check" checked={form.pinned} onChange={e => setForm({ ...form, pinned: e.target.checked })} style={{ accentColor: "var(--green)", width: 16, height: 16 }} />
              <label htmlFor="pin-check" style={{ fontSize: 13, fontWeight: 600, cursor: "pointer" }}>📌 {lang === "gr" ? "Καρφίτσωμα" : "Pin this"}</label>
            </div>

            <div style={{ display: "flex", gap: 9 }}>
              <button className="btn-base btn-green" style={{ flex: 1, justifyContent: "center" }} onClick={handleSave} disabled={saving}>
                {saving ? <LoadingPulse size={18} /> : editingId ? (lang === "gr" ? "Αποθήκευση" : "Save Changes") : (lang === "gr" ? "Δημοσίευση" : "Post")}
              </button>
              <button className="btn-base btn-outline" onClick={() => { setShowModal(false); setEditingId(null); setForm(emptyForm); resetAnnouncementImageState(); }}>
                {lang === "gr" ? "Ακύρωση" : "Cancel"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


